import React from "react";
import { FaTrash, FaPlus } from "react-icons/fa";
import PayoutTableCell from "./components/PayoutTableCell";
import {
  firstPlaceCapForPlaces,
  materializePlaceAmounts,
} from "../../utility/generateShapedPayoutTable";
import { PrimaryButton, SecondaryButton } from "../UI/DesignStandards";

const PayoutGrid = ({
  payoutData,
  isEditing,
  calculateColumnTotal,
  isColumnValid,
  onPercentageChange,
  onKeyDown,
  onAddColumn,
  onDeleteColumn,
  showColumnControls = false,
  prizeFund = 0,
  roundTo = 0,
  roundToWholeDollars = true,
  previewQuantity = 0,
  getColumnPrizeFund,
  lastPlaceFloor,
  isColumnFloorMiss,
  ratioNumerator,
  ratioDenominator,
  onSave,
  onCancel,
  saving = false,
  canSave = true,
  saveLabel,
}) => {
  const groupedByQuantity = payoutData.reduce(
    (acc, { payoutQuantity, percentage, payoutOrder }) => {
      if (!acc[payoutQuantity]) {
        acc[payoutQuantity] = [];
      }
      acc[payoutQuantity].push({ payoutOrder, percentage });
      return acc;
    },
    {}
  );

  const payoutOrders =
    payoutData.length > 0
      ? [...new Set(payoutData.map((payout) => payout.payoutOrder))].sort(
          (a, b) => a - b
        )
      : [];

  const payoutQuantities = Object.keys(groupedByQuantity)
    .map(Number)
    .sort((a, b) => a - b);

  if (payoutData.length === 0) {
    return (
      <div className="card-board p-8 text-center text-brand-muted">
        No percentages yet. Set places to cover in more options.
      </div>
    );
  }

  const handleCellKeyDown = (event, quantity, order) => {
    if (event.key !== "Tab") {
      onKeyDown?.(event, quantity, order);
      return;
    }

    const qty = Number(quantity);
    const index = payoutQuantities.indexOf(qty);
    let targetQuantity = qty;
    let targetOrder;

    if (event.shiftKey) {
      if (order > 1) {
        targetOrder = order - 1;
      } else if (index > 0) {
        targetQuantity = payoutQuantities[index - 1];
        targetOrder = targetQuantity;
      } else {
        return;
      }
    } else if (order < qty) {
      targetOrder = order + 1;
    } else if (index >= 0 && index < payoutQuantities.length - 1) {
      targetQuantity = payoutQuantities[index + 1];
      targetOrder = 1;
    } else {
      return;
    }

    event.preventDefault();
    const root = event.currentTarget.closest("table");
    const input = root?.querySelector(
      `input[data-quantity="${targetQuantity}"][data-order="${targetOrder}"]`
    );
    if (input) {
      input.focus();
      if (typeof input.select === "function") input.select();
    }
  };

  const fallbackFund = Number(prizeFund) || 0;
  const columnFund = (quantity) => {
    if (typeof getColumnPrizeFund === "function") {
      return Number(getColumnPrizeFund(quantity)) || 0;
    }
    if (
      Number(previewQuantity) > 0 &&
      Number(quantity) === Number(previewQuantity)
    ) {
      return fallbackFund;
    }
    return 0;
  };
  const anyDollars = payoutQuantities.some((quantity) => columnFund(quantity) > 0);
  const dollarsByColumn = {};
  payoutQuantities.forEach((quantity) => {
    const fund = columnFund(quantity);
    if (!(fund > 0)) return;
    dollarsByColumn[quantity] = new Map(
      materializePlaceAmounts({
        payoutData,
        quantity,
        prizeFund: fund,
        lastPlaceFloor,
        roundToWholeDollars,
        firstCap: firstPlaceCapForPlaces(
          quantity,
          ratioNumerator,
          ratioDenominator
        ),
        ratioNumerator,
        ratioDenominator,
        roundTo: roundToWholeDollars ? roundTo : undefined,
      }).map((item) => [item.place, item.amount])
    );
  });
  const anyFloorMiss =
    typeof isColumnFloorMiss === "function" &&
    payoutQuantities.some((quantity) => isColumnFloorMiss(quantity));
  const wide = payoutQuantities.length > 8;

  return (
    <div className="card-board overflow-hidden min-w-0 flex flex-col">
      <div className="px-4 py-3 border-b border-brand-charcoal/10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h3 className="heading-display text-lg">Places paid</h3>
          <p className="text-sm text-brand-muted">
            Each column is how many places are paid. Read down that
            column. Numbers are percent of the prize fund.
            {anyDollars
              ? roundToWholeDollars
                ? previewQuantity
                  ? " Dollar amounts on the gold column use this field's entries times prize per entry. They round to whole dollars. Leftover is paid out evenly to the top places until it is gone, skipping any place already at a cap."
                  : " Dollar amounts use the implied field for that column (places paid times the second number of the ratio, divided by the first). They round to whole dollars. Leftover is paid out evenly to the top places until it is gone, skipping any place already at a cap."
                : " Dollar amounts keep cents and total the prize fund."
              : ""}
            {anyFloorMiss
              ? " Amber marks a column whose last cash is under the last-place floor."
              : ""}
            {previewQuantity
              ? ` Gold is the ${previewQuantity}-place column for this field.`
              : ""}
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
          {wide ? (
            <p className="text-xs text-brand-muted">
              Scroll inside this table for more places.
            </p>
          ) : null}
          {isEditing && onSave ? (
            <div className="flex flex-wrap gap-2">
              <PrimaryButton
                onClick={onSave}
                disabled={saving || !canSave}
              >
                {saving ? "Saving..." : saveLabel || "Save"}
              </PrimaryButton>
              {onCancel ? (
                <SecondaryButton
                  onClick={onCancel}
                  className="!px-3 !py-2 text-sm"
                >
                  Cancel
                </SecondaryButton>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <div className="overflow-auto overscroll-contain w-full min-h-[24rem] max-h-[min(36rem,70vh)]">
        <table className="border-separate border-spacing-0 text-sm w-max">
          <thead>
            <tr>
              <th
                className="sticky top-0 left-0 z-30 bg-brand-charcoal text-brand-cream font-display font-bold text-xs tracking-wide px-3 py-2 text-left min-w-[3.5rem] shadow-[2px_0_0_0_rgba(0,0,0,0.08)]"
              >
                Place
              </th>
              {payoutQuantities.map((quantity) => {
                const invalid = isEditing && !isColumnValid(quantity);
                const highlighted =
                  Number(previewQuantity) > 0 &&
                  Number(quantity) === Number(previewQuantity);
                const floorMiss =
                  !invalid && Boolean(isColumnFloorMiss?.(quantity));
                return (
                  <th
                    key={quantity}
                    className={`sticky top-0 z-20 px-1.5 py-2 text-center min-w-[3.25rem] font-score ${
                      invalid
                        ? "bg-red-700 text-white"
                        : floorMiss
                          ? "bg-amber-600 text-white"
                          : highlighted
                            ? "bg-brand-gold text-brand-charcoal"
                            : "bg-brand-charcoal text-brand-cream"
                    }`}
                    title={
                      floorMiss
                        ? "Last cash is under the last-place floor"
                        : undefined
                    }
                  >
                    <div className="relative px-2">
                      {quantity}
                      <div
                        className={`text-[10px] font-normal leading-tight ${
                          invalid || floorMiss
                            ? "text-white"
                            : highlighted
                              ? "text-brand-charcoal/70"
                              : "text-brand-mist"
                        }`}
                      >
                        {(Number(calculateColumnTotal(quantity)) * 100).toFixed(
                          isEditing ? 2 : 1
                        )}
                        %
                      </div>
                      {isEditing && showColumnControls ? (
                        <button
                          type="button"
                          onClick={() => onDeleteColumn(quantity)}
                          className="absolute -top-1 -right-1 text-red-300 hover:text-red-100"
                          title={`Remove ${quantity} places`}
                        >
                          <FaTrash className="h-3 w-3" />
                        </button>
                      ) : null}
                    </div>
                  </th>
                );
              })}
              {isEditing && showColumnControls ? (
                <th className="sticky top-0 z-20 bg-brand-charcoal px-2 py-2">
                  <button
                    type="button"
                    onClick={onAddColumn}
                    className="text-brand-cream hover:text-brand-gold"
                    title="Add column"
                  >
                    <FaPlus className="h-4 w-4" />
                  </button>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {payoutOrders.map((order, rowIndex) => {
              const rowBg = rowIndex % 2 === 0 ? "bg-white" : "bg-brand-cream";
              return (
                <tr key={order} className="hover:bg-brand-gold/10">
                  <th
                    scope="row"
                    className={`sticky left-0 z-10 px-3 py-1.5 text-left font-score font-bold text-brand-charcoal ${rowBg} shadow-[2px_0_0_0_rgba(0,0,0,0.08)]`}
                  >
                    {order}
                  </th>
                  {payoutQuantities.map((quantity) => {
                    const matchingPayout = groupedByQuantity[quantity]?.find(
                      (payout) => payout.payoutOrder === order
                    );
                    const invalid =
                      isEditing && !isColumnValid(quantity);
                    const highlighted =
                      Number(previewQuantity) > 0 &&
                      Number(quantity) === Number(previewQuantity);
                    const fund = columnFund(quantity);
                    const floorMiss = Boolean(isColumnFloorMiss?.(quantity));
                    const dollar =
                      fund > 0 && matchingPayout && order <= quantity
                        ? dollarsByColumn[quantity]?.get(order) ?? null
                        : null;
                    return (
                      <PayoutTableCell
                        key={quantity}
                        isEditing={isEditing}
                        quantity={quantity}
                        order={order}
                        matchingPayout={matchingPayout}
                        onPercentageChange={onPercentageChange}
                        onKeyDown={handleCellKeyDown}
                        dollarAmount={dollar}
                        dollarWhole={roundToWholeDollars}
                        invalid={invalid}
                        rowBg={rowBg}
                        highlighted={highlighted}
                        floorMiss={floorMiss && order === Number(quantity)}
                      />
                    );
                  })}
                  {isEditing && showColumnControls ? (
                    <td className={`${rowBg} px-2`}></td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayoutGrid;
